import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {Button, Form, Input, InputNumber, Popconfirm, Select, Spin, Table, message} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {BCP_CHECK_JOB} from './graphql/queries/bcpCheckJobQueries';
import {
    CheckJobDetailResponse,
    CreateCheckJobDataSourceInput,
} from './graphql/types/BcpCheckJobTypes';
import {useBcpCheckUserGroups} from './hooks/useBcpCheckUserGroups';
import {CREATE_BCP_CHECK_JOB, UPDATE_BCP_CHECK_JOB} from './graphql/mutations/bcpCheckJobMutations';
import BcpDataSourceModal from './components/BcpDataSourceModal';
import BcpCheckJobDebugModal from './components/BcpCheckJobDebugModal';

interface DataSourceRow extends CreateCheckJobDataSourceInput {
    _key: number;
}

const BcpCheckJobFormPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const isEdit = Boolean(id);
    const jobId = id ? Number(id) : undefined;

    const [dataSources, setDataSources] = useState<DataSourceRow[]>([]);
    const [dsModalVisible, setDsModalVisible] = useState(false);
    const [editingDs, setEditingDs] = useState<{data?: CreateCheckJobDataSourceInput; index?: number}>({});
    const [keyCounter, setKeyCounter] = useState(0);
    const [debugModalOpen, setDebugModalOpen] = useState(false);

    const {data: detailData, loading: detailLoading} = useQuery<CheckJobDetailResponse>(BCP_CHECK_JOB, {
        variables: {id: jobId},
        skip: !isEdit || !jobId,
    });

    const userGroupOptions = useBcpCheckUserGroups();

    const [createJob, {loading: creating}] = useMutation(CREATE_BCP_CHECK_JOB);
    const [updateJob, {loading: updating}] = useMutation(UPDATE_BCP_CHECK_JOB);

    useEffect(() => {
        const job = detailData?.bcpCheckJob?.data;
        if (job) {
            form.setFieldsValue({
                name: job.name,
                remark: job.remark,
                checkRule: job.checkRule,
                windowTime: job.windowTime,
                alarmReceiver: job.alarmReceiver,
                alarmMode: job.alarmMode,
                sampleRatio: job.sampleRatio,
                userGroupId: job.userGroupId,
            });
            const sources = (job.dataSources || []).map((ds, idx) => ({
                type: ds.type ?? 0,
                config: ds.config,
                tableName: ds.tableName,
                useFields: ds.useFields,
                groupRule: ds.groupRule,
                filterRule: ds.filterRule,
                mustExist: ds.mustExist,
                _key: idx,
            }));
            setDataSources(sources);
            setKeyCounter(sources.length);
        }
    }, [detailData, form]);

    const handleAddDataSource = () => {
        setEditingDs({});
        setDsModalVisible(true);
    };

    const handleEditDataSource = (index: number) => {
        const ds = dataSources[index];
        setEditingDs({data: ds, index});
        setDsModalVisible(true);
    };

    const handleRemoveDataSource = (index: number) => {
        setDataSources((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDsConfirm = (data: CreateCheckJobDataSourceInput, index?: number) => {
        if (index !== undefined) {
            setDataSources((prev) =>
                prev.map((ds, i) => (i === index ? {...data, _key: ds._key} : ds)),
            );
        } else {
            setDataSources((prev) => [...prev, {...data, _key: keyCounter}]);
            setKeyCounter((c) => c + 1);
        }
        setDsModalVisible(false);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (dataSources.length === 0) {
                message.error('请至少添加一个数据源');
                return;
            }
            const input = {
                name: values.name,
                remark: values.remark || undefined,
                checkRule: values.checkRule || undefined,
                windowTime: values.windowTime || undefined,
                alarmReceiver: values.alarmReceiver || undefined,
                alarmMode: values.alarmMode ?? undefined,
                sampleRatio: values.sampleRatio ?? undefined,
                userGroupId: values.userGroupId || undefined,
                dataSources: dataSources.map(({_key, ...ds}) => ds),
            };
            if (isEdit && jobId) {
                const res = await updateJob({variables: {id: jobId, input}});
                const result = res.data?.updateBcpCheckJob;
                if (!result?.success) {
                    message.error(result?.message || '更新失败');
                    return;
                }
                message.success('更新成功');
            } else {
                const res = await createJob({variables: {input}});
                const result = res.data?.createBcpCheckJob;
                if (!result?.success) {
                    message.error(result?.message || '创建失败');
                    return;
                }
                message.success('创建成功');
                navigate('/ops-tool/settlement/bcp/check-jobs');
            }
        } catch (e: any) {
            message.error(e?.message || '操作失败');
        }
    };

    const dsColumns: ColumnsType<DataSourceRow> = [
        {title: '类型', dataIndex: 'type', key: 'type', width: 80, render: () => '数据库'},
        {
            title: '数据库',
            key: 'database',
            width: 150,
            render: (_: any, record: DataSourceRow) => {
                try {
                    const cfg = JSON.parse(record.config || '{}');
                    return cfg.database || '-';
                } catch { return '-'; }
            },
        },
        {title: '表名', dataIndex: 'tableName', key: 'tableName', width: 150},
        {title: '过滤规则', dataIndex: 'filterRule', key: 'filterRule', width: 150, ellipsis: true},
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, _record: DataSourceRow, index: number) => (
                <div className="flex gap-2">
                    <Button type="link" size="small" onClick={() => handleEditDataSource(index)}>
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除该数据源？"
                        onConfirm={() => handleRemoveDataSource(index)}
                        okText="确认"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    if (isEdit && detailLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-6 pb-6 pt-4">
            <div className="flex items-center gap-3 mb-4">
                <Button onClick={() => navigate(-1)}>返回</Button>
                <h2 className="text-base font-semibold m-0">
                    {isEdit ? '编辑核对任务' : '新建核对任务'}
                </h2>
            </div>

            <Form form={form} layout="vertical">
                <div className="max-w-2xl">
                    <Form.Item
                        label="任务名称"
                        name="name"
                        rules={[{required: true, message: '请输入任务名称'}]}
                    >
                        <Input placeholder="请输入任务名称" />
                    </Form.Item>
                    <Form.Item label="用户组" name="userGroupId" rules={[{required: true, message: '请选择用户组'}]}>
                        <Select
                            placeholder="请选择用户组"
                            options={userGroupOptions}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </div>

                <div className="mb-4 max-w-3xl">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium m-0">数据源列表</h3>
                        <Button size="small" onClick={handleAddDataSource}>添加数据源</Button>
                    </div>
                    <Table
                        dataSource={dataSources}
                        columns={dsColumns}
                        rowKey="_key"
                        pagination={false}
                        size="small"
                        locale={{emptyText: '暂无数据源，点击「添加数据源」添加'}}
                    />
                </div>

                <div className="max-w-2xl">
                    <Form.Item
                        label="核对规则"
                        name="checkRule"
                        rules={[{required: true, message: '请输入核对规则'}]}
                    >
                        <Input.TextArea rows={3} placeholder="请输入核对规则" />
                    </Form.Item>
                    <Form.Item
                        label="窗口时间(秒)"
                        name="windowTime"
                        rules={[{required: true, message: '请输入窗口时间'}]}
                    >
                        <InputNumber className="w-full" min={0} placeholder="请输入窗口时间" />
                    </Form.Item>
                    <Form.Item
                        label="告警接收人"
                        name="alarmReceiver"
                        rules={[{required: true, message: '请输入告警接收人'}]}
                    >
                        <Input placeholder="请输入告警接收人" />
                    </Form.Item>
                    <Form.Item
                        label="告警模式"
                        name="alarmMode"
                        rules={[{required: true, message: '请选择告警模式'}]}
                    >
                        <Select placeholder="请选择告警模式">
                            <Select.Option value={1}>立即告警</Select.Option>
                            <Select.Option value={2}>汇总告警</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="采样率(%)"
                        name="sampleRatio"
                        rules={[{required: true, message: '请输入采样率'}]}
                    >
                        <InputNumber className="w-full" min={0} max={100} placeholder="请输入采样率" />
                    </Form.Item>
                    <Form.Item
                        label="备注"
                        name="remark"
                        rules={[{required: true, message: '请输入备注'}]}
                    >
                        <Input.TextArea rows={2} placeholder="请输入备注" />
                    </Form.Item>
                </div>
            </Form>

            <div className="flex gap-2 mt-4">
                <Button
                    type="primary"
                    loading={creating || updating}
                    onClick={handleSubmit}
                >
                    保存
                </Button>
                <Button onClick={() => navigate(-1)}>返回</Button>
                {isEdit && jobId && (
                    <Button onClick={() => setDebugModalOpen(true)}>调试</Button>
                )}
            </div>

            <BcpDataSourceModal
                visible={dsModalVisible}
                initialData={editingDs.data}
                editIndex={editingDs.index}
                onConfirm={handleDsConfirm}
                onCancel={() => setDsModalVisible(false)}
            />
            {jobId && (
                <BcpCheckJobDebugModal
                    jobId={jobId}
                    open={debugModalOpen}
                    onClose={() => setDebugModalOpen(false)}
                />
            )}
        </div>
    );
};

export default BcpCheckJobFormPage;
